'use client';

import { useEffect, useState } from 'react';
import { ChangeStepFormEvent } from '@/components/getStarted/multi-step-form/multi-step-form.model';
import styles from './RangeStepInput.module.css';
import layoutStyles from '@/components/getStarted/multi-step-form/layout.module.css';

/**
 * helps to display the options information below the input.
 * @field inputValue represents a range value that matches the input values.
 * @field realValue represents any value different to inputValue.
 * @field label text|node to be displayed as option. If only text entered it may not
 * wrap the text, so you will need to add manually the node with the corresponding breaks
 */
export type RangeStepOption = {
  inputValue: string;
  realValue: string;
  label: React.ReactNode;
};
type Props = {
  options: RangeStepOption[];
  value?: string;
  name: string;
  onValueChange?: (value: RangeStepOption) => void;
  onChange: (value: ChangeStepFormEvent) => void;
};

export const RangeStepInput = ({
  value,
  onValueChange,
  options,
  name,
  onChange,
}: Props) => {
  const [range, setRange] = useState(() => (value ? value : '0'));
  const itemsCount = options.length;
  const steps = (itemsCount > 1 ? 100 / (itemsCount - 1) : 1).toFixed(2);

  const handleOnChange = (newValue: string) => {
    onChange({ target: { value: newValue, name } });
    if (onValueChange) {
      onValueChange(options.find((option) => option.inputValue === newValue)!);
    }
    setRange(newValue);
  };

  useEffect(() => {
    // update default value in form so required fields are not empty
    if (!value) {
      onChange({ target: { name, value: '0' } });
    }
  }, [range, value, onChange, name]);

  return (
    <div
      className={`${styles.rangeStepContainer} ${layoutStyles.item} max-w-[calc(100%_-_80px)] mx-auto xl:max-w-full`}
      style={
        {
          '--value-position': `${range}%`,
        } as React.CSSProperties
      }
    >
      <input
        name={name}
        type="range"
        id="range"
        className={`${styles.rangeInput} w-full`}
        value={range}
        step={steps}
        list="range-list"
        onChange={(e) => handleOnChange(e.target.value)}
      />
      <datalist id="range-list" className="flex relative">
        {options.map((option, index) => (
          <div
            key={option.realValue}
            style={{
              left: `${index * +steps}%`,
              position: 'absolute',
              transform: 'translateX(-50%)',
            }}
          >
            {option.label}
          </div>
        ))}
      </datalist>
      <div className="absolute w-full">
        {options.map((option, index) => (
          <div
            key={option.realValue}
            onClick={() => handleOnChange(option.inputValue)}
            style={{
              left: `${index * +steps}%`,
              top: '-15px',
              position: 'absolute',
              transform: 'translateX(-50%)',
              display: option.inputValue === range ? 'none' : '',
              backgroundColor: option.inputValue < range ? '#069183' : 'white',
              outlineColor: option.inputValue < range ? '#069183' : '#aaa',
            }}
            className={`${styles.rangeStepOption} transition-all duration-150`}
          ></div>
        ))}
      </div>
    </div>
  );
};
